<template>
    <NewDropdown
        ref="dropdown"
        class="TagSelectorDropdown"
        label="Select tags"
        multi-select
        :min-options-for-filter="0"
        persist-selected-items
        allow-empty
        hide-select-all
        popup-content-class="TagSelectorDropdown__popup"
        :options="tagOptions"
        :selected="selectedTagOptions"
        :disabled="disabled"
        @input="onTagsInput"
        @open="popupOpen = true"
        @close="popupOpen = false"
        @search-input="onSearchTextUpdate"
    >
        <template #option="scope">
            <NewDropdownSelectOption :scope="scope" multi-select>
                <template #label>
                    <span
                        class="TagSelectorDropdown__tagChip"
                        :class="getTagChipClass(scope.opt.isPredefined)"
                    >
                        {{ scope.opt.label }}
                    </span>
                </template>
            </NewDropdownSelectOption>
        </template>

        <template #selected-item="scope">
            <span
                class="TagSelectorDropdown__tagChip TagSelectorDropdown__tagChip--selected"
                :class="[getTagChipClass(scope.opt.isPredefined), { 'TagSelectorDropdown__tagChip--withRemove': popupOpen }]"
            >
                {{ scope.opt.label }}
                <NewButton
                    v-if="popupOpen"
                    class="TagSelectorDropdown__tagChipRemove"
                    variant="flat"
                    size="icon-xxs"
                    color="neutral"
                    @click.stop="scope.removeAtIndex(scope.index)"
                >
                    <EidIcon :icon-src="faXmark" />
                </NewButton>
            </span>
        </template>

        <template #after-options>
            <CreateTagOption
                v-if="createTagText"
                :text="createTagText"
                :max-length="maxTagLength"
                @create="onCreateTag"
            />
        </template>

        <template #no-option>
            <div class="TagSelectorDropdown__noOption">
                <span class="TagSelectorDropdown__noOptionText">No tag</span>
            </div>
            <CreateTagOption
                v-if="createTagText"
                :text="createTagText"
                :max-length="maxTagLength"
                @create="onCreateTag"
            />
        </template>
    </NewDropdown>
</template>

<style lang="scss">
.TagSelectorDropdown {
    &__popup {
        font-size: 0.8rem;

        // Override default green focused background for tag options
        .NewDropdownSelectOption.NewDropdownSelectOption--focused {
            .q-focus-helper {
                background: transparent !important;
            }

            .NewDropdownSelectOption__content {
                background: var(--eidBgGrayTones-lightest) !important;
            }
        }
    }

    &__tagChip {
        display: inline-flex;
        align-items: center;
        padding: 2px 10px;
        border-radius: 16px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        background-color: var(--eidSecureBlue-10);
        color: var(--eidSecureBlue-75);
        border: 1px solid var(--eidSecureBlue-25);

        &--user {
            background-color: var(--eidTrustGreen-10);
            border-color: var(--eidTrustGreen-25);
            color: var(--eidTrustGreen);
        }

        &--system {
            background-color: var(--eidSecureBlue-10);
            border-color: var(--eidSecureBlue-25);
            color: var(--eidSecureBlue-75);
        }

        &--selected {
            gap: 2px;
            margin: 2px 4px 2px 0;
            cursor: default;
        }

        &--withRemove {
            padding-right: 2px;
        }
    }

    &__noOption {
        padding: 8px 12px 4px;
    }

    &__noOptionText {
        color: var(--eidSecureBlue-50);
        font-size: 12px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import NewDropdownSelectOption
        from '@evidentid/dashboard-commons/components/Dropdown/NewDropdownSelectOption.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { ClientTag } from '@evidentid/tprm-portal-lib/models/activity-history';

    import NewButton from '@/components/new-button/NewButton.vue';

    import CreateTagOption from './CreateTagOption.vue';

    export default defineComponent({
        name: 'TagSelectorDropdown',
        components: {
            NewDropdown,
            NewDropdownSelectOption,
            NewButton,
            EidIcon,
            CreateTagOption,
        },
        props: {
            tags: {
                type: Array as PropType<ClientTag[]>,
                required: true,
            },
            selectedTags: {
                type: Array as PropType<string[]>,
                required: true,
            },
            loading: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'create-tag', 'select' ],
        data() {
            return {
                faXmark,
                maxTagLength: 25,
                popupOpen: false,
                searchText: '',
                localCreatedOptions: [] as (DropdownOption & { isPredefined: boolean })[],
                internalSelectedValues: [] as string[],
            };
        },
        computed: {
            tagOptions(): (DropdownOption & { isPredefined: boolean })[] {
                const apiOptions = this.tags.map((tag: ClientTag) => ({
                    value: tag.key,
                    label: tag.label,
                    isPredefined: tag.isPredefined,
                }));
                const apiLabels = new Set(apiOptions.map((o) => o.label));
                const extraLocal = (this.localCreatedOptions as (DropdownOption & { isPredefined: boolean })[])
                    .filter((o) => !apiLabels.has(o.label));
                return [ ...apiOptions, ...extraLocal ];
            },
            selectedTagOptions(): (DropdownOption & { isPredefined: boolean })[] {
                const optionMap = new Map(this.tagOptions.map((o) => [ o.value, o ]));
                return (this.internalSelectedValues as string[])
                    .map((value) => optionMap.get(value))
                    .filter(Boolean) as (DropdownOption & { isPredefined: boolean })[];
            },
            createTagText(): string {
                const trimmed = this.searchText.trim();
                if (!trimmed) {
                    return '';
                }
                const hasExactMatch = this.tagOptions.some(
                    (opt) => opt.label.toLowerCase() === trimmed.toLowerCase(),
                );
                return hasExactMatch ? '' : trimmed;
            },
        },
        watch: {
            selectedTags: {
                immediate: true,
                handler(newVal: string[]) {
                    this.internalSelectedValues = [ ...newVal ];
                },
            },
        },
        methods: {
            onSearchTextUpdate(text: string): void {
                this.searchText = text;
            },
            onTagsInput(options: DropdownOption[]): void {
                const values = options.map((o: DropdownOption) => o.value);
                (this as any).internalSelectedValues = values;
                this.$emit('select', values);
            },
            onCreateTag(label: string): void {
                const newOption = { value: label, label, isPredefined: false };
                const localOptions = this.localCreatedOptions as (DropdownOption & { isPredefined: boolean })[];
                if (!this.tagOptions.some((o) => o.value === label)) {
                    localOptions.push(newOption);
                }
                // Intentionally reading NewDropdown's internal `selectedOptions` via $refs.
                // NewDropdown (multi-select) emits @select on each option click but defers
                // @input until close/blur (applySelectionsMultiSelect). Between a tag creation
                // and the next @input, any intermediate selections made via @select are only
                // tracked inside NewDropdown.selectedOptions — they are not visible through
                // props or events. Reading the ref here is the only way to capture that state
                // so the emitted selection includes all checked items, not just what the last
                // @input reported.
                const dropdownRef = this.$refs.dropdown as any;
                const currentSelected: DropdownOption[] = dropdownRef?.selectedOptions ?? this.selectedTagOptions;
                const currentValues = currentSelected.map((o: DropdownOption) => o.value);
                if (!currentValues.includes(label)) {
                    currentValues.push(label);
                }
                (this as any).internalSelectedValues = currentValues;
                this.$emit('select', currentValues);
                this.$emit('create-tag', label);
                dropdownRef?.clearFilter();
            },
            getTagChipClass(isPredefined: boolean): Record<string, boolean> {
                return {
                    'TagSelectorDropdown__tagChip--system': isPredefined,
                    'TagSelectorDropdown__tagChip--user': !isPredefined,
                };
            },
        },
    });
</script>
